import React from 'react';
import { reverse } from 'named-urls';

import { Button } from 'components/Button';

import { useInfiniteSearch } from 'utils/hooks/infiniteSearch';

import CustomModal from 'components/CustomModal';

import { StyledTextField } from 'components/Input/styles';
import InfiniteAutocomplete from 'components/InfiniteAutocomplete';

import API from 'utils/api';

import routes from 'core/routes';

import { useStore } from 'utils/hooks/store';

import { APIRequest, SearchRequest, UuidableName } from 'utils/api/types';

import Logger from 'utils/logger';

import { InputHeader, StyledOption } from './styles';

interface StartAuditModalProps {
  open: boolean;
  onClose: () => void;
  relatedEntityUuid?: string;
}

const searchAuditTemplates = (params?: SearchRequest): APIRequest<UuidableName[]> =>
  API.entities.searchForms('audits', { ...params, ordering: 'name' });

// `/forms/search/` is server-side filtered to active templates, so archived ones never appear.
const StartAuditModal: React.FunctionComponent<StartAuditModalProps> = ({
  open,
  onClose,
  relatedEntityUuid,
}): React.ReactElement => {
  const { routing, notification } = useStore();
  const { results, loading, loadingMore, hasMore, loadMore, searchQuery } = useInfiniteSearch<UuidableName>(
    searchAuditTemplates,
    open
  );

  const [template, setTemplate] = React.useState<UuidableName | null>(null);
  const [starting, setStarting] = React.useState(false);

  const onStart = async (): Promise<void> => {
    if (!template) {
      return;
    }

    // Disabled while in flight: a double-click starts two audits, and the second create discards the
    // first draft while leaving its record behind, unfillable.
    setStarting(true);
    try {
      const { data } = await API.entities.createEntityFormsDrafts('audits', template.uuid, relatedEntityUuid)();
      routing.push(reverse(routes.dashboard.audits.draft.details, { draftId: data.uuid }));
    } catch (error) {
      Logger.error(error as string);
      notification.enqueueErrorSnackbar('Failed to start the audit');
    } finally {
      setStarting(false);
    }
  };

  return (
    <CustomModal
      openModal={open}
      onClose={onClose}
      modalTitle="Start audit"
      cancelButton={
        <Button
          id="cancel-start-audit-button"
          data-testid="cancel-start-audit-button"
          onClick={onClose}
          variant="outlined"
          size="large"
        >
          Cancel
        </Button>
      }
      submitButton={
        <Button
          id="start-audit-button"
          data-testid="start-audit-button"
          disabled={!template || starting}
          onClick={onStart}
          variant="contained"
          size="large"
        >
          Start audit
        </Button>
      }
    >
      <InputHeader>Template</InputHeader>
      <InfiniteAutocomplete
        loadingMore={loadingMore}
        hasMore={hasMore}
        onLoadMore={loadMore}
        id="audit-template-dropdown-field"
        data-testid="audit-template-dropdown-field"
        options={results}
        loading={loading}
        value={template}
        onChange={(_, selected) => setTemplate(selected as UuidableName | null)}
        isOptionEqualToValue={(option, selected) => (option as UuidableName).uuid === (selected as UuidableName).uuid}
        getOptionLabel={(option) => (option as UuidableName).name}
        renderInput={(params) => (
          <StyledTextField
            {...params}
            placeholder="Select Template"
            onChange={(e) => {
              searchQuery(e.target.value);
            }}
          />
        )}
        renderOption={(props, option) => <StyledOption {...props}>{(option as UuidableName).name}</StyledOption>}
        style={{
          height: 'fit-content',
          width: '100%',
        }}
        size="medium"
        sx={{
          '& + .MuiAutocomplete-popper .MuiAutocomplete-option': {
            fontSize: '16px',
          },
        }}
      />
    </CustomModal>
  );
};

export default StartAuditModal;
